import {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import {
  Column,
  columnFilteringFeature,
  ColumnFiltersState,
  columnResizingFeature,
  columnSizingFeature,
  ColumnSizingState,
  columnVisibilityFeature,
  ColumnVisibilityState,
  createColumnHelper,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  filterFn_includesString,
  filterFn_weakEquals,
  flexRender,
  OnChangeFn,
  PaginationState,
  rowPaginationFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_basic,
  sortFn_datetime,
  sortFn_text,
  SortingState,
  tableFeatures,
  useTable,
} from '@tanstack/react-table'
import {
  Badge,
  Group,
  Pagination,
  Paper,
  Select,
  Table,
  Text,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { useDebouncedValue } from '@mantine/hooks'
import { format } from 'date-fns'
import { DebouncedTextFilter } from '@components/general/DataTable/DebouncedTextFilter'
import {
  includesText,
  matchesDateRange,
} from '@components/general/DataTable/filters'
import {
  readStoredState,
  writeStoredState,
} from '@components/general/DataTable/storedState'
import {
  Ticket,
  TicketComment,
} from '@data/tickets/model/ticket'
import { calculateDurationInDays } from '@common/utils/helperFunctions'
import {
  getActionedByUsers,
  getLastUserEventAt,
} from '@data/tickets/ticketActivity'
import {
  IssueMap,
  priorityColors,
  statusColors,
} from './constants'
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Calendar,
  Circle,
  GripVertical,
  MessageSquare,
  Users,
} from 'lucide-react'
import { getDurationColor } from '@components/main/Home/Dashboard/helpers'
import './TicketTable.scss'

const COLUMN_VISIBILITY_KEY = 'ticket-list-column-visibility'
const COLUMN_SIZING_KEY = 'ticket-list-column-sizing'
const COLUMN_SIZING_PERSIST_DEBOUNCE_MS = 250
const SYSTEM_USER_ID = 1899
const formatTicketId = (id: number) => `#${String(id).padStart(6, '0')}`


interface TicketListViewProps {
  tickets: Ticket[]
  onTicketClick: (ticket: Ticket) => void
  statusFilter?: string | null
  priorityFilter?: string | null
  issueTypeFilter?: string | null
  onClearStatusFilter?: () => void
  onClearPriorityFilter?: () => void
  onClearIssueTypeFilter?: () => void
  onColumnVisibilityChange?: () => void
  // The dashboard's search box filters `tickets` before they reach the table. Passed so the
  // table can return to page 1 on a new search, which it cannot tell apart from a refresh.
  searchTerm?: string
}

export interface HideableColumn {
  id: string
  label: string
  visible: boolean
  toggle: (event?: unknown) => void
}

export interface TicketListViewRef {
  clearFilters: () => void
  getHideableColumns: () => HideableColumn[]
}

interface TicketTableRow extends Ticket {
  formattedId: string
  lastUserEventAt?: number
  mostRecentComment?: TicketComment
  actionedUsers: string[]
  actionedUsersSearchText: string
}

const features = tableFeatures({
  columnFilteringFeature,
  columnSizingFeature,
  columnResizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  rowPaginationFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  // Columns without an explicit sortFn/filterFn resolve via 'auto', which only
  // finds functions registered here. An unregistered name silently degrades.
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    basic: sortFn_basic,
    datetime: sortFn_datetime,
    text: sortFn_text,
  },
  filterFns: {
    includesString: filterFn_includesString,
    weakEquals: filterFn_weakEquals,
  },
})

const columnHelper = createColumnHelper<typeof features, TicketTableRow>()

// Mantine's DatePickerInput yields 'YYYY-MM-DD' strings. matchesDateRange reads them as local
// days; passing them to startOfDay/endOfDay directly read them as UTC midnight, which moved
// the range back a day for anyone west of UTC.
const matchesPickedDateRange = (value: unknown, filterValue: unknown) => {
  const [from, to] = (filterValue as [string | null, string | null] | undefined) ?? [null, null]
  return matchesDateRange(value as string, from, to)
}

// The shared text filter takes a value and a callback; this binds it to a TanStack column.
const renderTextFilter = (
  column: Column<typeof features, TicketTableRow>,
  placeholder: string,
) => (
  <DebouncedTextFilter
    ariaLabel={`Filter ${column.id}`}
    placeholder={placeholder}
    value={(column.getFilterValue() as string) ?? ''}
    onChange={(value) => column.setFilterValue(value)}
  />
)

export const TicketListView = forwardRef<TicketListViewRef, TicketListViewProps>(
  (
    {
      tickets,
      onTicketClick,
      statusFilter,
      priorityFilter,
      issueTypeFilter,
      onClearStatusFilter,
      onClearPriorityFilter,
      onClearIssueTypeFilter,
      onColumnVisibilityChange,
      searchTerm = '',
    },
    ref,
  ) => {
    const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }])
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
    const [pagination, setPagination] = useState<PaginationState>({
      pageIndex: 0,
      pageSize: 25,
    })

    const defaultColumnVisibility: ColumnVisibilityState = {
      description: false,
      issueStartDate: false,
      // createdAt: false,
      assignedTo: false,
      eventDate: false,
    }

    const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>(() =>
      readStoredState(COLUMN_VISIBILITY_KEY, defaultColumnVisibility),
    )
    const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
      readStoredState(COLUMN_SIZING_KEY, {}),
    )
    const [debouncedColumnSizing] = useDebouncedValue(
      columnSizing,
      COLUMN_SIZING_PERSIST_DEBOUNCE_MS,
    )

    const handleColumnVisibilityChange: OnChangeFn<ColumnVisibilityState> = updater => {
      setColumnVisibility(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater
        writeStoredState(COLUMN_VISIBILITY_KEY, next)
        return next
      })
    }

    const handleColumnSizingChange: OnChangeFn<ColumnSizingState> = updater => {
      setColumnSizing(prev => {
        return typeof updater === 'function' ? updater(prev) : updater
      })
    }

    useEffect(() => {
      writeStoredState(COLUMN_SIZING_KEY, debouncedColumnSizing)
    }, [debouncedColumnSizing])

    useEffect(() => {
      const externalFilterIds = new Set(['status', 'priority', 'issueType'])

      setColumnFilters(prev => {
        const next = prev.filter(filter => !externalFilterIds.has(filter.id))
        const addExternalFilter = (id: string, value?: string | null) => {
          if (value && value !== 'all') {
            next.push({ id, value })
          }
        }

        addExternalFilter('status', statusFilter)
        addExternalFilter('priority', priorityFilter)
        addExternalFilter('issueType', issueTypeFilter)

        return next
      })
    }, [statusFilter, priorityFilter, issueTypeFilter])

    // The table's automatic page reset is off (`autoResetPageIndex: false`), because it fired
    // on every data change: closing or updating a ticket, Refresh, and the background refetch
    // all sent the user back to page 1. Only a change to what the table shows does that now:
    // the sort, a column filter (including the dashboard's status/priority/issue type, which
    // arrive as column filters above) or the search. Keyed by value, not identity, so a filter
    // re-set to the same value does not count as a change.
    const resultSetKey = JSON.stringify([sorting, columnFilters, searchTerm])
    useEffect(() => {
      setPagination(prev => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))
    }, [resultSetKey])


    const uniqueIssueTypes = useMemo(() => {
      const types = [...new Set(tickets.map(t => t.issueType))]
      return types.map(type => ({ value: type, label: IssueMap[type] }))
    }, [tickets])

    const uniquePriorities = useMemo(() => {
      return [...new Set(tickets.map(t => t.priority))].map(p => ({
        value: p,
        label: p,
      }))
    }, [tickets])

    const uniqueStatuses = useMemo(() => {
      return [...new Set(tickets.map(t => t.status))].map(s => ({
        value: s,
        label: s,
      }))
    }, [tickets])

    const uniqueDeviceIds = useMemo(() => {
      return [...new Set(tickets.map(t => t.deviceId))].map(d => ({
        value: d,
        label: d,
      }))
    }, [tickets])

    const uniqueDeviceNames = useMemo(() => {
      return [...new Set(tickets.map(t => t.deviceName))].map(d => ({
        value: d,
        label: d,
      }))
    }, [tickets])

    const dUuidTicketCounts = useMemo(() => {
      const counts = new Map<string, number>()
      for (const ticket of tickets) {
        counts.set(ticket.dUuid, (counts.get(ticket.dUuid) ?? 0) + 1)
      }
      return counts
    }, [tickets])

    const tableData = useMemo<TicketTableRow[]>(() => {
      return tickets.map(ticket => {
        let mostRecentComment: TicketComment | undefined

        for (const comment of ticket.comments ?? []) {
          if (comment.deleted) continue

          if (
            !mostRecentComment ||
            new Date(comment.date).getTime() >
            new Date(mostRecentComment.date).getTime()
          ) {
            mostRecentComment = comment
          }
        }

        const lastUserEventAt = getLastUserEventAt(ticket, [SYSTEM_USER_ID])
        const actionedUsers = getActionedByUsers(ticket, [SYSTEM_USER_ID])
        // const actionedUsersSearchText = actionedUsers.length > 0 ? actionedUsers.join(' ') : ticket.userName ?? ''
        const actionedUsersSearchText = actionedUsers.length > 0 ? actionedUsers.join(' ') : ''

        return {
          ...ticket,
          formattedId: formatTicketId(ticket.id),
          lastUserEventAt,
          mostRecentComment,
          actionedUsers,
          actionedUsersSearchText,
        }
      })
    }, [tickets])

    const columns = useMemo(
      () => columnHelper.columns([
        columnHelper.accessor('formattedId', {
          id: 'id',
          header: 'Ticket ID',
          enableHiding: false,
          cell: info => (
            <Text inherit fw={500} fs='italic'>
              {info.getValue()}
            </Text>
          ),
          size: 100,
        }),

        columnHelper.accessor('deviceId', {
          header: 'Device ID',
          enableHiding: false,
          cell: info => (
            <Text inherit fw={500}>
              {info.getValue()}
            </Text>
          ),
          size: 150,
        }),

        columnHelper.accessor('deviceName', {
          header: 'Device Name',
          cell: info => (
            <Text inherit fw={500}>
              {info.getValue()}
            </Text>
          ),
          size: 350,
        }),

        columnHelper.display({
          id: 'multipleTickets',
          header: 'Multi-Ticket',
          cell: info => {
            const count = dUuidTicketCounts.get(info.row.original.dUuid) ?? 1
            return count > 1 ? (
              <Badge
                color='orange'
                variant='light'
                size='md'
              >
                {count} tickets
              </Badge>
            ) : (
              <Text inherit c='dimmed'>-</Text>
            )
          },
          filterFn: (row, _columnId, filterValue) => {
            if (!filterValue || filterValue === 'all') return true
            const count = dUuidTicketCounts.get(row.original.dUuid) ?? 1
            return count > 1
          },
          size: 120,
        }),

        columnHelper.accessor('issueType', {
          header: 'Issue Type',
          enableHiding: false,
          cell: info => (
            // <Badge
            //   variant='light'
            //   color='gray'
            //   size='md'
            // >
            //   {IssueMap[info.getValue()]}
            // </Badge>
            <Text inherit>
              {IssueMap[info.getValue()]}
            </Text>
          ),
          size: 150,
        }),

        columnHelper.accessor('priority', {
          header: 'Priority',
          cell: info => (
            <Badge
              color={priorityColors[info.getValue()]}
              variant='light'
              size='md'
              leftSection={
                info.getValue() === 'high' ? <AlertCircle size={12} /> : undefined
              }
            >
              {info.getValue()}
            </Badge>
          ),
          size: 100,
        }),

        columnHelper.accessor('status', {
          header: 'Status',
          enableHiding: false,
          cell: info => (
            <Badge
              color={statusColors[info.getValue()]}
              variant='light'
              size='md'
              leftSection={
                <Circle size={8} fill='currentColor' />
              }
            >
              {info.getValue().replaceAll('_', ' ')}
            </Badge>
          ),
          size: 140,
        }),

        columnHelper.accessor('description', {
          header: 'Description',
          cell: info => (
            <Text inherit fw={500} fs='italic'>
              {info.getValue()}
            </Text>
          ),
          size: 100,
        }),

        columnHelper.accessor('issueStartDate', {
          header: 'Duration',
          id: 'durationSinceStart',
          cell: info => {
            const days = calculateDurationInDays(info.getValue())

            return (
              <Badge
                color={getDurationColor(days)}
                variant='light'
                size='md'
                leftSection={
                  <Circle
                    size={8}
                    fill='currentColor'
                  />
                }
              >
                {days} {days === 1 ? 'day' : 'days'}
              </Badge>
            )
          },
          size: 150,
        }),

        columnHelper.accessor('issueStartDate', {
          header: 'Start Date',
          cell: info => (
            <Text inherit>
              {format(new Date(info.getValue()), 'dd-MM-yyyy HH:mm')}
            </Text>
          ),
          filterFn: (row, columnId, filterValue) =>
            matchesPickedDateRange(row.getValue(columnId), filterValue),
          size: 150,
        }),

        columnHelper.accessor('createdAt', {
          header: 'Ticket Created',
          cell: info => (
            <Text inherit>
              {format(new Date(info.getValue()), 'dd-MM-yyyy HH:mm')}
            </Text>
          ),
          filterFn: (row, columnId, filterValue) =>
            matchesPickedDateRange(row.getValue(columnId), filterValue),
          size: 150,
        }),

        columnHelper.accessor('lastUserEventAt', {
          header: 'Last Updated',
          id: 'durationSinceLastEvent',
          cell: info => {
            const lastUserEventAt = info.getValue()

            if (!lastUserEventAt) {
              return (
                <Text inherit c='dimmed'>
                  -
                </Text>
              )
            }

            const days = calculateDurationInDays(
              new Date(lastUserEventAt).toISOString(),
            )

            return (
              <Badge
                color={getDurationColor(days)}
                variant='light'
                size='md'
                leftSection={<Circle size={8} fill='currentColor' />}
              >
                {days === 0
                  ? 'Today'
                  : `${days} day${days === 1 ? '' : 's'}`
                }
              </Badge>
            )
          },
          sortFn: (rowA, rowB) => {
            const a = rowA.original.lastUserEventAt
            const b = rowB.original.lastUserEventAt

            if (!a && !b) return 0
            if (!a) return 1
            if (!b) return -1

            return b - a
          },
          sortDescFirst: false,
          size: 140,
        }),

        columnHelper.accessor('eventDate', {
          header: 'Last Update Date',
          cell: (info) => (
            <Text inherit>
              {format(new Date(info.getValue()), 'dd-MM-yyyy HH:mm')}
            </Text>
          ),
          filterFn: (row, columnId, filterValue) =>
            matchesPickedDateRange(row.getValue(columnId), filterValue),
          size: 140,
        }),

        columnHelper.display({
          id: 'comment',
          header: 'Recent Comment',
          cell: info => {
            const { mostRecentComment } = info.row.original

            return mostRecentComment ? (
              <Group
                gap={2}
                wrap='nowrap'
              >
                <MessageSquare size={12} color='gray' />
                <Text
                  inherit
                  lineClamp={1}
                  style={{ maxWidth: 200 }}
                >
                  {mostRecentComment.comment}
                </Text>
              </Group>
            ) : (
              <Text inherit c='dimmed'>
                -
              </Text>
            )
          },
          // A ticket with no comment matches no search, as before.
          filterFn: (row, _columnId, filterValue) =>
            includesText(row.original.mostRecentComment?.comment, filterValue),
          size: 220,
        }),

        columnHelper.display({
          id: 'assignedTo',
          header: 'Assigned To',
          cell: info => {
            const ticket = info.row.original
            const { actionedUsers } = ticket
            const hasUsers = actionedUsers.length > 0

            return hasUsers ? (
              <Group gap={2} wrap='nowrap'>
                <Users size={12} />
                <Text inherit c='dimmed' lineClamp={1}>
                  {actionedUsers.join(', ')}
                </Text>
              </Group>
            ) : (
              <Text inherit c='dimmed'>
                -
              </Text>
            )
          },
          filterFn: (row, _columnId, filterValue) =>
            includesText(row.original.actionedUsersSearchText, filterValue),
          size: 220,
        }),
      ]),
      [dUuidTicketCounts],
    )

    const table = useTable({
      features,
      data: tableData,
      columns,
      state: {
        sorting,
        columnFilters,
        columnVisibility,
        columnSizing,
        pagination,
      },
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onColumnVisibilityChange: handleColumnVisibilityChange,
      onColumnSizingChange: handleColumnSizingChange,
      onPaginationChange: setPagination,
      autoResetPageIndex: false,
      columnResizeMode: 'onChange',
    })

    const hideableColumns = useMemo(
      () => table.getAllLeafColumns().filter(col => col.getCanHide()),
      [table],
    )

    useImperativeHandle(ref, () => ({
      clearFilters: () => setColumnFilters([]),
      getHideableColumns: () =>
        hideableColumns.map(col => ({
          id: col.id,
          label: typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id,
          visible: col.getIsVisible(),
          toggle: col.getToggleVisibilityHandler(),
        })),
    }), [hideableColumns])

    useEffect(() => {
      onColumnVisibilityChange?.()
    }, [columnVisibility, onColumnVisibilityChange])

    const paginatedRows = table.getRowModel().rows
    const filteredRows = table.getPrePaginatedRowModel().rows
    const filteredRowCount = filteredRows.length
    const visibleColumnCount = table.getVisibleLeafColumns().length
    const pageCount = table.getPageCount()

    // Closing the only ticket on the last page would otherwise leave the table on an empty
    // page past the end. Step back to the new last page instead.
    useEffect(() => {
      const lastPageIndex = Math.max(0, pageCount - 1)
      if (pagination.pageIndex > lastPageIndex) {
        setPagination(prev => ({ ...prev, pageIndex: lastPageIndex }))
      }
    }, [pageCount, pagination.pageIndex])
    const firstVisibleRow =
      filteredRowCount === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1
    const lastVisibleRow = Math.min(
      pagination.pageIndex * pagination.pageSize + paginatedRows.length,
      filteredRowCount,
    )

    return (
      <Paper
        shadow='sm'
        p='md'
        radius='md'
        withBorder
        className='ticket-table'
        data-density='sm'
      >
        <div
          style={{
            overflowX: 'auto',
            maxHeight: 'calc(100vh - 400px)',
            position: 'relative',
          }}
        >
          <Table
            striped
            highlightOnHover
            style={{
              tableLayout: 'fixed',
              width: table.getTotalSize(),
              minWidth: 1200,
            }}
          >
            <Table.Thead
              style={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 1,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {table.getHeaderGroups().map(headerGroup => (
                <Fragment key={headerGroup.id}>
                  <Table.Tr key={`${headerGroup.id}-header`}>
                    {headerGroup.headers.map(header => (
                      <Table.Th
                        key={header.id}
                        style={{
                          width: header.getSize(),
                          cursor: header.column.getCanSort() ? 'pointer' : 'default',
                          userSelect: 'none',
                          backgroundColor: 'white',
                          position: 'relative',
                          overflow: 'hidden',
                        }}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <Group gap='xs' wrap='nowrap'>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}

                          {header.column.getIsSorted() === 'asc' ? (
                            <ArrowUp size={14} />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <ArrowDown size={14} />
                          ) : null}
                        </Group>

                        {header.column.getCanResize() && (
                          <div
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            onClick={e => e.stopPropagation()}
                            style={{
                              position: 'absolute',
                              right: 0,
                              top: 0,
                              height: '100%',
                              width: 16,
                              cursor: 'col-resize',
                              userSelect: 'none',
                              touchAction: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRight: header.column.getIsResizing()
                                ? '2px solid var(--mantine-color-blue-5)'
                                : '1px solid var(--mantine-color-gray-3)',
                              background: header.column.getIsResizing()
                                ? 'var(--mantine-color-blue-0)'
                                : 'linear-gradient(90deg, transparent, var(--mantine-color-gray-0))',
                              color: header.column.getIsResizing()
                                ? 'var(--mantine-color-blue-6)'
                                : 'var(--mantine-color-gray-5)',
                            }}
                            title='Drag to resize column'
                          >
                            <GripVertical size={12} />
                          </div>
                        )}
                      </Table.Th>
                    ))}
                  </Table.Tr>

                  <Table.Tr key={`${headerGroup.id}-filter`}>
                    {headerGroup.headers.map(header => (
                      <Table.Th
                        key={`${header.id}-filter`}
                        style={{
                          width: header.getSize(),
                          backgroundColor: 'white',
                          padding: '0.5rem',
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        {header.column.id === 'id' && (
                          renderTextFilter(header.column, 'Filter...')
                        )}

                        {header.column.id === 'deviceId' && (
                          <Select
                            placeholder='All'
                            size='xs'
                            data={uniqueDeviceIds}
                            value={(header.column.getFilterValue() as string) ?? null}
                            onChange={value => header.column.setFilterValue(value)}
                            clearable
                            searchable
                          />
                        )}

                        {header.column.id === 'deviceName' && (
                          <Select
                            placeholder='All'
                            size='xs'
                            data={uniqueDeviceNames}
                            value={(header.column.getFilterValue() as string) ?? null}
                            onChange={value => header.column.setFilterValue(value)}
                            clearable
                            searchable
                          />
                        )}

                        {header.column.id === 'multipleTickets' && (
                          <Select
                            placeholder='All'
                            size='xs'
                            data={[
                              { value: 'all', label: 'All' },
                              { value: 'yes', label: 'Multiple only' },
                            ]}
                            value={(header.column.getFilterValue() as string) ?? null}
                            onChange={value => header.column.setFilterValue(value)}
                            clearable
                          />
                        )}

                        {header.column.id === 'issueType' && (
                          <Select
                            placeholder='All'
                            size='xs'
                            data={uniqueIssueTypes}
                            value={(header.column.getFilterValue() as string) ?? null}
                            onChange={value => {
                              header.column.setFilterValue(value)

                              if (!value && onClearIssueTypeFilter) {
                                onClearIssueTypeFilter()
                              }
                            }}
                            clearable
                          />
                        )}

                        {header.column.id === 'priority' && (
                          <Select
                            placeholder='All'
                            size='xs'
                            data={uniquePriorities}
                            value={(header.column.getFilterValue() as string) ?? null}
                            onChange={value => {
                              header.column.setFilterValue(value)

                              if (!value && onClearPriorityFilter) {
                                onClearPriorityFilter()
                              }
                            }}
                            clearable
                          />
                        )}

                        {header.column.id === 'status' && (
                          <Select
                            placeholder='All'
                            size='xs'
                            data={uniqueStatuses}
                            value={(header.column.getFilterValue() as string) ?? null}
                            onChange={value => {
                              header.column.setFilterValue(value)

                              if (!value && onClearStatusFilter) {
                                onClearStatusFilter()
                              }
                            }}
                            clearable
                          />
                        )}

                        {header.column.id === 'description' && (
                          renderTextFilter(header.column, 'Filter...')
                        )}

                        {(header.column.id === 'createdAt' ||
                          header.column.id === 'eventDate' ||
                          header.column.id === 'issueStartDate') && (
                          <DatePickerInput
                            aria-label={`Filter ${header.column.id} by date range`}
                            type='range'
                            placeholder='Select range...'
                            size='xs'
                            value={
                              (header.column.getFilterValue() as [
                                  string | null,
                                  string | null,
                              ]) ?? [null, null]
                            }
                            onChange={value => header.column.setFilterValue(value)}
                            leftSection={<Calendar size={12} />}
                            clearable
                          />
                        )}

                        {header.column.id === 'comment' && (
                          renderTextFilter(header.column, 'Search...')
                        )}

                        {header.column.id === 'assignedTo' && (
                          renderTextFilter(header.column, 'Search...')
                        )}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                </Fragment>
              ))}
            </Table.Thead>

            <Table.Tbody>
              {paginatedRows.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={visibleColumnCount}>
                    <div
                      style={{
                        textAlign: 'center',
                        padding: '2rem 0',
                      }}
                    >
                      <Text size='sm' c='dimmed'>
                        No tickets found
                      </Text>
                    </div>
                  </Table.Td>
                </Table.Tr>
              ) : (
                paginatedRows.map(row => (
                  <Table.Tr
                    key={row.id}
                    onClick={() => onTicketClick(row.original)}
                    style={{ cursor: 'pointer' }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <Table.Td
                        key={cell.id}
                        className='ticket-table-cell'
                        style={{
                          width: cell.column.getSize(),
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </div>

        {tickets.length > 0 && (
          <Group
            justify='space-between'
            mt='md'
            pt='md'
            style={{
              borderTop: '1px solid #e9ecef',
            }}
          >
            <Group gap='xs'>
              <Text size='sm' c='dimmed'>
                Rows per page
              </Text>
              <Select
                size='xs'
                w={80}
                data={['10', '25', '50', '100']}
                value={String(pagination.pageSize)}
                onChange={value => {
                  if (value) table.setPageSize(Number(value))
                }}
                allowDeselect={false}
              />
            </Group>

            <Text size='sm' c='dimmed'>
              Showing {firstVisibleRow}-{lastVisibleRow} of {filteredRowCount} ticket
              {filteredRowCount !== 1 ? 's' : ''}
            </Text>

            {pageCount > 1 ? (
              <Pagination
                size='sm'
                total={pageCount}
                value={pagination.pageIndex + 1}
                onChange={page => table.setPageIndex(page - 1)}
              />
            ) : null}
          </Group>
        )}
      </Paper>
    )
  },
)

TicketListView.displayName = 'TicketListView'
